/**
 * Left rail — always-visible status pillars driven by live data from
 * the console-worker via the global store.
 *
 * Each widget reads its slice from the cache, renders skeletons during
 * the initial fetch, and shows degraded placeholders when the data
 * source isn't running (mac/vps reporter offline).
 */
import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import { renderMorningBriefPanel } from "./MorningBrief";
import { renderDailyRitualPanel } from "./DailyRitual";
import { renderWorkersPanel } from "./WorkersPanel";
import { openLaunchAgentLog } from "./LaunchAgentLogDrawer";
import type {
  MacStatus, VpsStatus, TodaysSlateSummary, AnomalyBadges,
  CostSpendSnapshot, LaunchAgentStatus, ActivityEvent,
} from "../data/types";

export function renderLeftRail(): TemplateResult {
  return html`
    <aside class="left-rail">
      ${renderMorningBriefPanel()}
      ${renderDailyRitualPanel()}
      ${renderWorkersPanel()}
      ${launchAgentsPanel()}
      ${todaysSlatePanel()}
      ${spendPanel()}
      ${anomaliesPanel()}
      ${activityTickerPanel()}
    </aside>
  `;
}

// =============================================================
// VPS panel
// =============================================================
export function renderVpsPanel(): TemplateResult { return vpsPanel(); }
function vpsPanel(): TemplateResult {
  const e = store.get<VpsStatus & { _stale?: boolean }>("vpsStatus");
  if (e.error) return panelError("VPS scraper", e.error);
  if (!e.data && e.loading) return panelSkeleton("VPS scraper", 2);
  if (!e.data) return panelStub("VPS scraper", "no data — VPS reporter offline");

  const v = e.data;
  if (v._stale) return panelStub("VPS scraper", v.hostname);

  const lastScrapeMs = v.last_scrape_at ? Date.parse(v.last_scrape_at) : 0;
  const ageMin = lastScrapeMs ? Math.floor((Date.now() - lastScrapeMs) / 60_000) : -1;
  // FIX-19 (2026-05-11): smarter health signal. The 5-min systemd gate
  // ALWAYS fires every 5 min — that's the true "scraper alive" proxy.
  // last_scrape_at (= newest hr-*.json mtime) can lag for legit reasons:
  // HR's GraphQL rate-limits the VPS IP, the scrape still runs but
  // discover returns 0 events. Before this fix, that scenario flashed
  // RED at 15+ min even though the scraper itself was healthy.
  //   scraper-alive  = gate fired in the last 10 min  → ≥ warn, not bad
  //   data-fresh     = files written in the last 6 min → green
  const lastGateMs = v.last_gate_fire_at ? Date.parse(v.last_gate_fire_at) : 0;
  const gateAgeMin = lastGateMs ? Math.floor((Date.now() - lastGateMs) / 60_000) : -1;
  const scraperAlive = gateAgeMin >= 0 && gateAgeMin <= 10;
  const rateLimited  = scraperAlive && ageMin > 15;
  const dotCls =
    ageMin < 0 && !scraperAlive ? "is-bad"
    : ageMin >= 0 && ageMin <= 6 ? "is-ok"
    : rateLimited                ? "is-warn"
    : ageMin >= 0 && ageMin <= 15 ? "is-warn"
    : scraperAlive               ? "is-warn"
    : "is-bad";
  const cadenceLabel: Record<string, string> = {
    prime: "PRIME", "off-peak": "OFF-PEAK", stalled: "STALLED", unknown: "UNKNOWN",
  };
  // Status label varies based on the health classification.
  const ageLabel =
    ageMin < 0 && !scraperAlive ? "no scrapes"
    : rateLimited               ? `data stale ${ageMin}m — HR rate-limit`
    : ageMin < 0                ? `gate alive (no data yet)`
    : `last scrape ${ageMin}m ago`;
  return html`
    <section class="panel">
      <div class="panel__title">
        <span>VPS scraper</span>
        <span class="panel__title-actions">${v.hostname}</span>
      </div>
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:8px;">
        <span class="status-dot ${dotCls} ${(ageMin >= 0 && ageMin <= 6) ? "is-pulse" : ""}"></span>
        <span style="color:var(--text-primary);font-weight:600;">
          ${cadenceLabel[v.cadence] ?? v.cadence}
        </span>
        <span style="margin-left:auto;color:var(--text-tertiary);font-size:11px;
                      font-variant-numeric:tabular-nums;">
          ${ageLabel}
        </span>
      </div>
      <div style="font-size:12px;color:var(--text-tertiary);">
        Disk: <span style="color:var(--text-secondary);">${v.disk_free_gb} GB free</span> ·
        RAM: <span style="color:var(--text-secondary);">${v.memory_free_gb} GB</span>
      </div>
      ${v.skip_reason
        ? html`<div style="margin-top:6px;font-size:11px;color:var(--accent-orange-light);">
                 ⚠ ${v.skip_reason}
               </div>`
        : ""}
    </section>
  `;
}

// =============================================================
// LaunchAgent grid
// =============================================================
export function renderLaunchAgentsPanel(): TemplateResult { return launchAgentsPanel(); }
function launchAgentsPanel(): TemplateResult {
  const e = store.get<MacStatus>("macStatus");
  if (e.error) return panelError("LaunchAgents", e.error);
  if (!e.data && e.loading) return panelSkeleton("LaunchAgents", 3);
  if (!e.data) return panelStub("LaunchAgents", "Mac reporter not running");

  const agents = e.data.launchagents ?? [];
  const loaded = agents.filter((a) => a.loaded).length;
  const failed = agents.filter((a) => a.last_exit_code != null && a.last_exit_code !== 0).length;
  return html`
    <section class="panel">
      <div class="panel__title">
        <span>LaunchAgents · ${loaded} loaded</span>
        <span class="panel__title-actions
                      ${failed > 0 ? "" : ""}"
              style="color:${failed > 0 ? "var(--accent-red-light)" : "var(--accent-green-light)"};">
          ${failed > 0 ? `${failed} failed` : "all green"}
        </span>
      </div>
      <div style="display:grid;grid-template-columns:repeat(9,1fr);gap:6px;">
        ${agents.map(agentDot)}
      </div>
    </section>
  `;
}

function agentDot(a: LaunchAgentStatus): TemplateResult {
  const cls =
    !a.loaded                                       ? "is-mute"
    : a.last_exit_code != null && a.last_exit_code !== 0 ? "is-bad"
    : "is-ok";
  const short = a.label.replace("com.yurysankov.", "");
  const next = a.next_fire ? new Date(a.next_fire).toLocaleTimeString([], {
    hour: "2-digit", minute: "2-digit",
  }) : "—";
  return html`
    <button class="agent-dot-btn"
            title="${short} · ${a.schedule_label} · next ${next} · click for log"
            @click=${() => openLaunchAgentLog(short)}>
      <span class="status-dot ${cls}"></span>
    </button>
  `;
}

// =============================================================
// Today's slate
// =============================================================
export function renderTodaysSlatePanel(): TemplateResult { return todaysSlatePanel(); }
function todaysSlatePanel(): TemplateResult {
  const e = store.get<TodaysSlateSummary>("todaysSlate");
  if (e.error) return panelError("Today's slate", e.error);
  if (!e.data && e.loading) return panelSkeleton("Today's slate", 5);
  if (!e.data) return panelStub("Today's slate", "no data");

  const total = e.data.by_sport.reduce((s, b) => s + b.posted, 0);
  const colorOf: Record<string, string> = {
    NBA:    "var(--accent-orange-light)",
    WNBA:   "#2dd4bf",
    MLB:    "var(--accent-blue-light)",
    NHL:    "var(--accent-purple-light)",
    NFL:    "#d4a017",
    SOCCER: "var(--accent-green-light)",
    UFC:    "var(--accent-red-light)",
  };
  return html`
    <section class="panel">
      <div class="panel__title">
        <span>Today's slate</span>
        <span class="panel__title-actions">${total} posted</span>
      </div>
      ${e.data.by_sport.map((b) => html`
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;
                    font-variant-numeric:tabular-nums;">
          <span style="flex-shrink:0;width:48px;font-size:11px;font-weight:600;
                        color:${colorOf[b.sport] ?? "var(--text-secondary)"};
                        letter-spacing:0.04em;text-transform:uppercase;">
            ${b.sport}
          </span>
          <span style="font-size:13px;color:var(--text-primary);font-weight:500;">
            ${b.posted}
          </span>
          <span style="font-size:11px;color:var(--text-tertiary);">
            · ${b.graded}/${b.posted} graded
          </span>
          ${b.pnl_units != null ? html`
            <span style="margin-left:auto;font-size:11px;
                          color:${b.pnl_units >= 0 ? "var(--accent-green-light)" : "var(--accent-red-light)"};">
              ${b.pnl_units >= 0 ? "+" : ""}${b.pnl_units}u
            </span>
          ` : ""}
        </div>
      `)}
    </section>
  `;
}

// =============================================================
// Anthropic spend
// =============================================================
export function renderSpendPanel(): TemplateResult { return spendPanel(); }
function spendPanel(): TemplateResult {
  // Phase 3.A: live aggregation from R2 usage logs (console-worker only;
  // picker/grader integration lands in 3.D).
  const e = store.get<CostSpendSnapshot>("costSpend");
  if (e.error) return panelError("Anthropic · today", e.error);
  if (!e.data && e.loading) return panelSkeleton("Anthropic", 1);
  if (!e.data) return panelStub("Anthropic · today", "no data");

  const s = e.data;
  const todayN = s.window_total.requests > 0
    ? s.series.find((d) => d.date === s.today)?.requests ?? 0
    : 0;
  const cacheRatePct = s.cache_hit_rate != null
    ? Math.round(s.cache_hit_rate * 100) : 0;

  // First-fire empty state — log file doesn't exist yet
  if (s.window_total.requests === 0) {
    return html`
      <section class="panel">
        <div class="panel__title">
          <span>Anthropic · today</span>
          <span class="panel__title-actions">tracker armed</span>
        </div>
        <div style="font-size:13px;color:var(--text-quaternary);line-height:1.5;">
          No Claude calls yet. Trigger the first ✨ button or Cmd-K
          query and a record lands here within 30s.
        </div>
      </section>
    `;
  }

  const deltaCls = s.delta_vs_7d_usd > 0.20 ? "is-bad-text"
                : s.delta_vs_7d_usd < -0.20 ? "is-ok-text" : "";

  return html`
    <section class="panel">
      <div class="panel__title">
        <span>Anthropic · today</span>
        <span class="panel__title-actions">cache ${cacheRatePct}%</span>
      </div>
      <div style="font-size:28px;font-weight:700;color:var(--text-primary);
                  font-variant-numeric:tabular-nums;letter-spacing:-0.02em;">
        $${s.today_cost_usd.toFixed(2)}
      </div>
      <div style="font-size:11px;color:var(--text-tertiary);margin-top:2px;
                   font-variant-numeric:tabular-nums;">
        ${todayN} call${todayN === 1 ? "" : "s"} ·
        <span class="${deltaCls}">
          ${s.delta_vs_7d_usd >= 0 ? "+" : ""}$${s.delta_vs_7d_usd.toFixed(2)} vs 7d
        </span>
      </div>
      <div style="font-size:10px;color:var(--text-quaternary);margin-top:4px;">
        ~$${s.projection_month_usd.toFixed(2)} projected this month
      </div>
    </section>
  `;
}

// =============================================================
// Anomalies
// =============================================================
export function renderAnomaliesPanel(): TemplateResult { return anomaliesPanel(); }
function anomaliesPanel(): TemplateResult {
  const e = store.get<AnomalyBadges>("anomalies");
  if (e.error) return panelError("Anomalies", e.error);
  if (!e.data && e.loading) return panelSkeleton("Anomalies", 1);
  if (!e.data) return panelStub("Anomalies", "no data — anomalies endpoint not responding");

  const badges = e.data.badges ?? [];
  if (badges.length === 0) {
    return html`
      <section class="panel">
        <div class="panel__title"><span>Anomalies · 0</span></div>
        <div style="font-size:12px;color:var(--accent-green-light);
                    display:flex;align-items:center;gap:8px;">
          <span class="status-dot is-ok is-pulse"></span>
          All systems nominal
        </div>
      </section>
    `;
  }
  const palette: Record<string, [string, string]> = {
    info:  ["rgba(10, 132, 255, 0.08)",  "rgba(10, 132, 255, 0.20)"],
    warn:  ["rgba(251, 191, 36, 0.08)",  "rgba(251, 191, 36, 0.20)"],
    error: ["rgba(255, 69, 58, 0.10)",   "rgba(255, 69, 58, 0.24)"],
  };
  return html`
    <section class="panel">
      <div class="panel__title"><span>Anomalies · ${badges.length}</span></div>
      <div style="display:flex;flex-direction:column;gap:6px;">
        ${badges.map((b) => {
          const pair = palette[b.severity] ?? palette["warn"]!;
          const [bg, br] = pair;
          const dotCls = b.severity === "error" ? "is-bad" : "is-warn";
          return html`
            <div style="display:flex;gap:8px;padding:8px 10px;
                          background:${bg};border:1px solid ${br};
                          border-radius:var(--radius-md);">
              <span class="status-dot ${dotCls}" style="margin-top:5px;flex-shrink:0;"></span>
              <div style="font-size:12px;line-height:1.4;min-width:0;flex:1;">
                <div style="color:var(--text-primary);font-weight:500;">${b.title}</div>
                <div style="color:var(--text-tertiary);font-size:11px;
                              white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                  ${b.detail}
                </div>
              </div>
            </div>
          `;
        })}
      </div>
    </section>
  `;
}

// =============================================================
// Helpers
// =============================================================
function panelSkeleton(title: string, rows: number): TemplateResult {
  return html`
    <section class="panel">
      <div class="panel__title"><span>${title}</span></div>
      ${Array.from({ length: rows }, () => html`
        <div class="skeleton" style="margin-bottom:6px;height:14px;"></div>
      `)}
    </section>
  `;
}

function panelStub(title: string, msg: string): TemplateResult {
  return html`
    <section class="panel">
      <div class="panel__title">
        <span>${title}</span>
        <span class="phase-stub-badge">offline</span>
      </div>
      <div style="font-size:12px;color:var(--text-quaternary);">
        ${msg}
      </div>
    </section>
  `;
}

function panelError(title: string, err: Error): TemplateResult {
  return html`
    <section class="panel" style="border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>${title}</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="font-size:11px;color:var(--accent-red-light);
                  line-height:1.4;font-family:monospace;
                  word-break:break-all;white-space:pre-wrap;">
        ${err.message}
      </div>
    </section>
  `;
}

// =============================================================
// Activity ticker — moved from right rail (Phase 6 layout)
// =============================================================
const ACTIVITY_CATEGORY_COLOR: Record<string, string> = {
  scrape:      "var(--accent-blue-light)",
  picker:      "var(--accent-purple-light)",
  grader:      "var(--accent-green-light)",
  guardrail:   "var(--accent-orange-light)",
  sync:        "var(--text-tertiary)",
  deploy:      "var(--accent-purple-light)",
  outcome:     "var(--accent-green-light)",
  alert:       "var(--accent-red-light)",
  probables:   "var(--accent-amber)",
  data:        "var(--accent-blue-light)",
  telemetry:   "var(--text-tertiary)",
  infra:       "var(--text-quaternary)",
  calibration: "var(--accent-amber)",
  other:       "var(--text-quaternary)",
};

export function renderActivityTickerPanel(): TemplateResult { return activityTickerPanel(); }
function activityTickerPanel(): TemplateResult {
  const e = store.get<ActivityEvent[]>("activity");

  return html`
    <section class="panel"
             style="${e.error ? "border-color:rgba(255,69,58,0.32);" : ""}">
      <div class="panel__title">
        <span>Live activity</span>
        <span class="panel__title-actions">
          ${e.error
            ? html`<span style="color:var(--accent-red-light);">fetch error</span>`
            : e.fetched_at
              ? `${fmtAge(Date.now() - e.fetched_at)}`
              : "loading…"}
        </span>
      </div>
      ${e.error ? html`
        <div style="font-size:11px;color:var(--accent-red-light);
                    line-height:1.4;font-family:monospace;
                    word-break:break-all;white-space:pre-wrap;">
          ${e.error.message}
        </div>
      ` : ""}
      ${e.loading && !e.data ? activitySkeleton() : ""}
      ${e.data && e.data.length === 0 ? html`
        <div style="font-size:12px;color:var(--text-quaternary);
                     padding:6px 0;">
          No events yet.
        </div>
      ` : ""}
      ${e.data && e.data.length > 0 ? html`
        <div style="display:flex;flex-direction:column;gap:0;
                     max-height:420px;overflow-y:auto;
                     font-variant-numeric:tabular-nums;
                     margin:0 -4px;">
          ${e.data.slice(0, 40).map((ev) => activityRow(ev))}
        </div>
      ` : ""}
    </section>
  `;
}

function activityRow(ev: ActivityEvent): TemplateResult {
  const color = ACTIVITY_CATEGORY_COLOR[ev.category] ?? "var(--text-secondary)";
  const t = new Date(ev.ts);
  const time = isNaN(t.getTime()) ? "—" : t.toLocaleTimeString([], {
    hour: "2-digit", minute: "2-digit",
  });
  // Two-line layout: time + category up top, message below — tighter
  // for the 280px left rail vs the previous 420px right rail.
  return html`
    <div style="padding:6px 4px;
                 border-bottom:1px solid var(--border-1);
                 font-size:12px;line-height:1.4;"
         title="${ev.details ?? ev.message}">
      <div style="display:flex;align-items:center;gap:6px;
                   margin-bottom:2px;">
        <span style="color:var(--text-quaternary);font-size:10px;
                      font-variant-numeric:tabular-nums;">${time}</span>
        <span style="color:${color};font-size:9px;
                      letter-spacing:0.06em;text-transform:uppercase;
                      font-weight:600;">
          ${ev.category}
        </span>
      </div>
      <div style="color:var(--text-secondary);font-size:11.5px;
                   overflow:hidden;text-overflow:ellipsis;
                   display:-webkit-box;-webkit-line-clamp:2;
                   -webkit-box-orient:vertical;">
        ${ev.message}
      </div>
    </div>
  `;
}

function activitySkeleton(): TemplateResult {
  return html`
    <div style="display:flex;flex-direction:column;gap:8px;">
      ${Array.from({ length: 5 }, () => html`
        <div class="skeleton" style="height:14px;"></div>
      `)}
    </div>
  `;
}

function fmtAge(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60)  return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}
